import { DashboardState, ESdChangeReason } from "@kuika/kuika-cl-model";
import {
  ECardinality,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IManagedDbDataModel,
  IManagedDbDeleteModel,
  ISymbol
} from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import {
  formatQualifiedName,
  getTableDatasourceQualifiers
} from "../../../../shared/utility/datasource-name-qualifier";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import ActionParameterTitle from "./action-parameter-title";
import {
  findSelectedManagedDbTable,
  IManagedDbTable,
  IManagedDbTableSelection,
  isSelectedManagedDbTable
} from "./managed-db-table-selection";
import { SymbolPicker } from "./symbol-picker";

export interface IManagedDbDeleteSymbolGroupState {}

export interface IManagedDbDeleteSymbolGroupProps {
  action: IAction;
  managedDbData?: IManagedDbDataModel;
  updateManagedDbData: (managedDbData: IManagedDbDataModel, changeReason?: ESdChangeReason) => void;
}

interface ITable {
  id?: string;
  key: string;
  title: string;
  schema: string;
  columns: any;
  dropped: boolean;
  dsId: string;
}

/** Delete Record's name comparison, as it always was; the datasource is settled by the caller. */
function isDeleteTableNameMatch(table: IManagedDbTable, selection: IManagedDbTableSelection): boolean {
  return (
    table.title?.toLowerCase() == selection.selectedTableName?.toLowerCase() &&
    table.schema?.toLowerCase() == selection.selectedTableSchema?.toLowerCase()
  );
}

class ManagedDbDeleteSymbolGroup extends PureSdComponent<
  IManagedDbDeleteSymbolGroupProps & IWithSDProps,
  IManagedDbDeleteSymbolGroupState
> {
  constructor(props: IManagedDbDeleteSymbolGroupProps & IWithSDProps) {
    super(props);
    this.state = {};
  }

  componentDidMount = () => {
    if (!this.props.managedDbData?.delete) {
      const emptyManagedDbData: IManagedDbDeleteModel = {
        selectedTableName: undefined,
        selectedDatasourceId: undefined,
        idParameter: this.getEmptyIdParameter()
      };
      this.updateManagedDbDeleteData(emptyManagedDbData);
    } else {
      this.updateDeleteDataIfNeeded();
    }
  };

  updateDeleteDataIfNeeded = () => {
    const tables = this.getTables();
    const deleteData = this.props.managedDbData?.delete;
    if (!deleteData || !tables || tables.length === 0) return;
    let tableNameChanged = false;

    // update table name if renamed..
    if (deleteData.selectedTableId) {
      const table = tables.find((x) => x.id === deleteData.selectedTableId);
      const tableName = table?.title;
      if (tableName && tableName?.length > 0) {
        if (tableName !== deleteData.selectedTableName) {
          // tableNameChanged = true;
        }
      }
    }

    if (tableNameChanged) {
      this.updateManagedDbDeleteData(deleteData);
    }
  };

  updateManagedDbDeleteData = (deleteData: IManagedDbDeleteModel) => {
    const managedDbData: IManagedDbDataModel = {
      delete: deleteData
    };
    this.props.updateManagedDbData(managedDbData);
  };

  handleSelectTable = (key: string): void => {
    const tables = this.getTables();
    const selectedTable = tables.find((x) => x.key === key);
    const deleteData = this.props.managedDbData?.delete;
    // Same table only when the current selection resolves to this very entry: a same-named table of
    // another datasource is a different table, and picking it must switch (KOA-18147).
    if (
      !deleteData ||
      !selectedTable ||
      isSelectedManagedDbTable(tables, deleteData, selectedTable, isDeleteTableNameMatch)
    ) {
      return;
    }
    const emptyManagedDbData: IManagedDbDeleteModel = {
      selectedTableName: selectedTable?.title,
      selectedTableSchema: selectedTable?.schema,
      selectedTableId: selectedTable?.id,
      selectedDatasourceId: selectedTable?.dsId,
      idParameter: this.getEmptyIdParameter()
    };
    this.updateManagedDbDeleteData(emptyManagedDbData);
  };

  getEmptyIdParameter = (): IActionParameter => {
    const typeName = KMainFunctions.getTypeNameBySqlDataType("uniqueidentifier");
    const symbol = {
      category: ESymbolCategory.fixed,
      fixedValue: ""
    } as ISymbol;
    const parameter: IActionParameter = {
      name: "Id",
      symbol,
      Typename: typeName,
      cardinality: ECardinality.One
    };
    return parameter;
  };

  handleSelectSymbol(symbol: ISymbol, changeReason: ESdChangeReason | undefined): void {
    const deleteData = this.props.managedDbData?.delete;
    if (deleteData) {
      deleteData.idParameter.symbol = symbol;
      this.updateManagedDbDeleteData(deleteData);
    }
  }

  getTables = (): ITable[] => {
    const result: ITable[] = [];
    const { schemas } = this.props.sd;
    const dsIdsProcessed: string[] = [];
    const defaultKuikaTableNames = KMainFunctions.getDefaultKuikaTableNames();

    Array.from(schemas.values())?.forEach((schema) => {
      if (dsIdsProcessed.indexOf(schema.datasourceId) != -1) return;
      dsIdsProcessed.push(schema.datasourceId);
      schema.tables.forEach((table) => {
        if (defaultKuikaTableNames.indexOf(table.title) === -1) {
          result.push({ ...table, dsId: schema.datasourceId });
        }
      });
    });

    return result;
  };

  handleRedirectToDatasources = () => {
    this.props.sd.setDashboardState(DashboardState.dataDiagramScheme);
  };

  render() {
    const tables = this.getTables();
    const deleteData = this.props.managedDbData?.delete;
    if (!deleteData || !tables || tables.length === 0)
      return (
        <span style={{ fontSize: 10 }}>
          You don't have any tables at the moment. Please{" "}
          <a onClick={this.handleRedirectToDatasources}>create a table</a> before to be able to use DeleteRecord.
        </span>
      );
    const selectedTable: ITable | undefined = findSelectedManagedDbTable(tables, deleteData, isDeleteTableNameMatch);
    const parameter = deleteData.idParameter;
    // A table name another datasource also holds is shown as "[CRM_DB] Users" (KOA-18147).
    const tableQualifiers: Map<string, string> = getTableDatasourceQualifiers(tables, this.props.sd.datasources);
    const tableOptions: KuikaSelectboxOption[] = tables.map((table: ITable): KuikaSelectboxOption => ({
      key: table.key,
      value: table.key,
      label: formatQualifiedName(table.title, tableQualifiers.get(table.key))
    }));

    return (
      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <div className="kuika_symbol-picker__managed-db">
          <KuikaSelectbox
            searchable
            placeholder="Select Table"
            className="kuika__action-parameters__item-select"
            value={selectedTable?.key ?? null}
            onChange={this.handleSelectTable}
            dropdownMatchSelectWidth={false}
            options={tableOptions}
          />

          <div className="kuika_property-grid-column">
            <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
              <ActionParameterTitle parameter={parameter} />
            </span>
            {
              <SymbolPicker
                parameter={parameter}
                action={this.props.action}
                changeType="onBlur"
                symbol={parameter.symbol}
                valueType={parameter.Typename}
                targetCardinality={parameter.cardinality}
                onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
                  this.handleSelectSymbol(symbol, changeReason)
                }
              />
            }
          </div>
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.WFStepMappingChange ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const managedDbDeleteSymbolGroup = withSD<IManagedDbDeleteSymbolGroupProps>(ManagedDbDeleteSymbolGroup);
export { managedDbDeleteSymbolGroup as ManagedDbDeleteSymbolGroup };
